import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { AlertTriangle, ArrowRight, BadgeCheck, Camera, Eye, FileImage, Globe2, ImagePlus, ScanLine, ShieldCheck, Sparkles, Tags } from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";

const sampleImage = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80";

const copy = {
  zh: {
    brand: "启序视界",
    lang: "EN",
    nav: ["能力", "分析", "治理"],
    label: "独立案例站点 06 / 多模态 AI 图像应用",
    title: "一个能看图、打标和给出审核建议的 AI 图像网站。",
    subtitle: "面向电商、内容运营和企业质检场景，输入图片 URL 或场景描述，生成视觉摘要、主体识别、业务标签和风险提示。",
    primary: "开始分析",
    secondary: "查看能力",
    featuresTitle: "图像理解能力",
    featuresSubtitle: "把图片从素材变成可检索、可审核、可运营的数据资产。",
    features: [
      ["主体识别", "识别商品、环境、人物动作和关键视觉元素。"],
      ["业务打标", "输出电商类目、营销场景、内容标签和搜索关键词。"],
      ["审核风险", "提示肖像、商标、敏感场景和夸大宣传风险。"],
      ["运营建议", "生成 ALT 文本、标题方向和素材复用建议。"]
    ],
    labTitle: "多模态分析工作台",
    labSubtitle: "后端直连 MiniMax；有图片 URL 时进行图像分析，没有图片时按场景描述模拟分析。",
    imageUrl: "图片 URL",
    scene: "场景描述",
    task: "分析任务",
    analyze: "分析图像",
    sample: "填入示例",
    waiting: "等待图片或场景输入。",
    governanceTitle: "视觉 AI 治理边界",
    governanceSubtitle: "真实图像应用需要明确可判断内容和必须人工审核的边界。",
    panels: [
      ["可追溯输入", "保存原图 URL、分析任务和模型输出，方便复核。"],
      ["人工审核", "人物肖像、商标、医疗金融承诺进入人工审核。"],
      ["资产沉淀", "把标签、ALT 文本和标题建议同步到素材库。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 6 个真实独立网站"
  },
  en: {
    brand: "Qixu Vision Lab",
    lang: "中文",
    nav: ["Features", "Analyze", "Govern"],
    label: "Standalone Case Site 06 / Multimodal AI Vision",
    title: "An AI vision site for image understanding, tagging and review.",
    subtitle: "For commerce, content ops and quality inspection: analyze image URLs or scene descriptions into summaries, labels and risk notes.",
    primary: "Analyze now",
    secondary: "View features",
    featuresTitle: "Vision Capabilities",
    featuresSubtitle: "Turn images into searchable, reviewable and operational data assets.",
    features: [
      ["Object recognition", "Identify products, environments, actions and key visual elements."],
      ["Business tagging", "Generate categories, marketing scenarios, content tags and search keywords."],
      ["Review risks", "Flag portrait, trademark, sensitive scene and exaggerated claim risks."],
      ["Ops actions", "Generate ALT text, title angles and asset reuse suggestions."]
    ],
    labTitle: "Multimodal Analysis Desk",
    labSubtitle: "The backend calls MiniMax directly. Image URL triggers visual analysis; text-only input uses scene simulation.",
    imageUrl: "Image URL",
    scene: "Scene description",
    task: "Analysis task",
    analyze: "Analyze image",
    sample: "Use sample",
    waiting: "Waiting for an image or scene.",
    governanceTitle: "Vision AI Governance",
    governanceSubtitle: "Real vision apps need clear boundaries between model judgment and human review.",
    panels: [
      ["Traceable input", "Store source URL, task and model output for review."],
      ["Human review", "Portraits, trademarks, medical or financial claims require review."],
      ["Asset memory", "Sync tags, ALT text and title suggestions to the asset library."]
    ],
    footer: "Qixu Technology AI case site · sixth real standalone website"
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [imageUrl, setImageUrl] = useState(sampleImage);
  const [scene, setScene] = useState("一张用于 SaaS 官网的产品工作台截图，包含数据图表、任务列表和深色侧边栏。");
  const [task, setTask] = useState("商品图审核与营销解读");
  const [result, setResult] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setScene(next === "zh" ? "一张用于 SaaS 官网的产品工作台截图，包含数据图表、任务列表和深色侧边栏。" : "A SaaS product dashboard screenshot with charts, task lists and a dark sidebar.");
    setTask(next === "zh" ? "商品图审核与营销解读" : "Image review and marketing analysis");
    setResult(copy[next].waiting);
    setMeta("");
  }

  async function analyze() {
    if ((!imageUrl.trim() && !scene.trim()) || loading) return;
    setLoading(true);
    setResult(locale === "zh" ? "正在请求 MiniMax 分析图像..." : "Requesting MiniMax vision analysis...");
    setMeta("");
    try {
      const response = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl, scene, task, locale })
      });
      const data = await response.json();
      setResult(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setResult(locale === "zh" ? "网络异常，无法完成图像分析。" : "Network error. Analysis unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top"><span>视</span><strong>{t.brand}</strong></a>
        <nav><a href="#features">{t.nav[0]}</a><a href="#lab">{t.nav[1]}</a><a href="#governance">{t.nav[2]}</a></nav>
        <button className="lang" onClick={switchLocale}><Globe2 size={16} />{t.lang}</button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#lab">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#features">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><Eye size={22} />Vision pipeline</div>
          <div className="pipeline">
            <div><ImagePlus size={18} /> Image input</div>
            <div><ScanLine size={18} /> Visual analysis</div>
            <div><Tags size={18} /> Business tags</div>
            <div><ShieldCheck size={18} /> Risk review</div>
          </div>
        </div>
      </section>

      <section className="features" id="features">
        <div className="section-copy"><h2>{t.featuresTitle}</h2><p>{t.featuresSubtitle}</p></div>
        <div className="feature-grid">
          {[Camera, Tags, AlertTriangle, BadgeCheck].map((Icon, index) => (
            <article key={t.features[index][0]}><Icon size={24} /><h3>{t.features[index][0]}</h3><p>{t.features[index][1]}</p></article>
          ))}
        </div>
      </section>

      <section className="lab" id="lab">
        <div className="desk">
          <div className="section-title"><FileImage size={22} /><div><h2>{t.labTitle}</h2><p>{t.labSubtitle}</p></div></div>
          <label>{t.imageUrl}<input value={imageUrl} onChange={(event) => setImageUrl(event.target.value)} /></label>
          <label>{t.scene}<textarea value={scene} onChange={(event) => setScene(event.target.value)} /></label>
          <label>{t.task}<input value={task} onChange={(event) => setTask(event.target.value)} /></label>
          <div className="composer-actions">
            <button onClick={() => { setImageUrl(sampleImage); setScene(locale === "zh" ? "一张用于 SaaS 官网的产品工作台截图，包含数据图表、任务列表和深色侧边栏。" : "A SaaS product dashboard screenshot with charts, task lists and a dark sidebar."); }}>{t.sample}</button>
            <button className="primary" onClick={analyze} disabled={loading || (!imageUrl.trim() && !scene.trim())}>{t.analyze}<Sparkles size={16} /></button>
          </div>
        </div>
        <div className="preview-card">
          <div className="image-frame">{imageUrl ? <img src={imageUrl} alt="analysis target" /> : <FileImage size={54} />}</div>
          <div className="result-card">
            <h3>Vision output</h3>
            <p>{result}</p>
            {meta && <span>{meta}</span>}
          </div>
        </div>
      </section>

      <section className="governance" id="governance">
        <div><h2>{t.governanceTitle}</h2><p>{t.governanceSubtitle}</p></div>
        <div className="govern-grid">
          {t.panels.map(([title, content]) => <article key={title}><ShieldCheck size={20} /><h3>{title}</h3><p>{content}</p></article>)}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
